/**
 * Home — "The Briefing" page.
 *
 * A time-aware editorial landing surface modeled after the mockup at
 * `/mockups/home.html`. Surfaces:
 *
 *   1. Time-aware greeting (serif H1 34px) with day eyebrow.
 *   2. Brief prose paragraph summarizing overnight agent activity.
 *   3. Alerts section: horizontal scrollable briefing cards with agent dots.
 *   4. Agent Insights: compact horizontal strip of mini cards.
 *   5. Recent Conversations: clickable chips that navigate to /play.
 *   6. Stats strip: 4-cell grid (serif numbers, no outer border).
 *
 * Tour wiring and page-enter animation preserved from the previous version.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Calendar,
  CheckCircle,
  ChevronDown,
  GitPullRequest,
  Rocket,
  Shield,
  Ticket,
  Unplug,
  Video,
  Workflow,
} from 'lucide-react';

import { usePageEnter, useStaggerAnimation } from '@/hooks/useAppleMotion';
import { AppleButton } from '@/components/atoms/AppleButton';
import { GuidedTour, useTour } from '@/components/tour';
import { homeTourSteps, TOUR_START_STORAGE_KEY } from './Home.tour';
import { useAuth } from '@/contexts/AuthContext';

import type {
  BriefingCard,
  RecentConversation,
  HomeStats,
} from '@/api/services/stats.service';

// ---------------------------------------------------------------------------
// Mock Data (inline — matches stats.service.ts shapes)
// ---------------------------------------------------------------------------

const MOCK_ALERTS: ReadonlyArray<BriefingCard> = [
  {
    id: 'critical-pipeline',
    headline: 'Pipeline failure — prod-deploy',
    subtitle: 'All targets failed 12 minutes ago',
    critical: true,
    pills: [
      { label: 'View pipeline', seed: 'View pipeline failure prod-deploy all targets failed' },
      { label: 'Rollback', seed: 'Rollback prod-deploy pipeline failure' },
      { label: 'Dismiss', seed: 'Dismiss prod-deploy pipeline alert' },
    ],
  },
  {
    id: 'agent-developer',
    agentId: 'developer',
    agentName: 'Developer',
    headline: 'Stale CRs need your review',
    subtitle: 'CR-274716 is 3 days old, CR-275001 is 5 days',
    count: 3,
    pills: [
      { label: 'Review 274716', seed: 'Review CR-274716 stale 3 days' },
      { label: 'Review 275001', seed: 'Review CR-275001 stale 5 days' },
      { label: 'Merge 274901', seed: 'Merge approved CR-274901' },
    ],
  },
  {
    id: 'agent-security',
    agentId: 'security',
    agentName: 'Security',
    headline: 'New SAS finding detected',
    subtitle: 'Token refresh vulnerability (medium severity)',
    count: 2,
    pills: [
      { label: 'View finding', seed: 'View SAS finding token refresh vulnerability' },
      { label: 'Acknowledge', seed: 'Acknowledge SAS finding token refresh' },
    ],
  },
  {
    id: 'agent-devops',
    agentId: 'devops',
    agentName: 'DevOps',
    headline: 'Beta deploy latency elevated',
    subtitle: 'P95 up 40% since Wednesday',
    count: 1,
    pills: [
      { label: 'Investigate', seed: 'Investigate beta deploy latency P95 up 40%' },
    ],
  },
  {
    id: 'agent-pm',
    agentId: 'analyst',
    agentName: 'Project Manager',
    headline: 'Sprint at risk',
    subtitle: '8 points remaining, deadline Thursday',
    count: 2,
    pills: [
      { label: 'View sprint', seed: 'View sprint status 8 points remaining deadline Thursday' },
      { label: 'Adjust scope', seed: 'Adjust sprint scope deadline Thursday' },
    ],
  },
  {
    id: 'agent-support',
    agentId: 'support-engineer',
    agentName: 'Support Engineer',
    headline: 'Oncall handoff tomorrow',
    subtitle: 'Prep checklist available',
    count: 1,
    pills: [
      { label: 'View checklist', seed: 'View oncall handoff prep checklist' },
    ],
  },
];


const MOCK_CONVERSATIONS: ReadonlyArray<RecentConversation> = [
  { id: 'conv-1', agentId: 'developer', title: 'Auth flow investigation', time: '2h ago', seed: 'Continue auth flow investigation' },
  { id: 'conv-2', agentId: 'devops', title: 'Pipeline deploy latency', time: 'yesterday', seed: 'Continue pipeline deploy latency discussion' },
  { id: 'conv-3', agentId: 'analyst', title: 'Sprint planning Phase 2', time: '3d ago', seed: 'Continue sprint planning phase 2' },
];

const MOCK_STATS: HomeStats = {
  tokensToday: 12847,
  sessionsToday: 8,
  invocations: 34,
  activeTasks: 3,
  agentsActive: 9,
};

interface ScheduleMeeting {
  id: string;
  time: string;
  title: string;
  duration: string;
  prep: string;
  joinable: boolean;
}

const MOCK_SCHEDULE: ReadonlyArray<ScheduleMeeting> = [
  {
    id: 'meeting-1',
    time: '10:00 AM',
    title: 'Sprint 14 Standup',
    duration: '15min',
    prep: '3 blockers to raise, Sprint velocity at 82%',
    joinable: true,
  },
  {
    id: 'meeting-2',
    time: '11:30 AM',
    title: 'Security Review: ASR Findings',
    duration: '30min',
    prep: '4 medium-severity findings pending, token cache poisoning remediation due Friday',
    joinable: false,
  },
  {
    id: 'meeting-3',
    time: '2:00 PM',
    title: 'DSE Leadership Review',
    duration: '60min',
    prep: 'Demo Wayne multi-agent orchestration, show Tasks dashboard',
    joinable: false,
  },
  {
    id: 'meeting-4',
    time: '4:30 PM',
    title: '1:1 with rampugal',
    duration: '30min',
    prep: 'Discuss A5 OBO prod enablement timeline, AgentCore traffic shift plan',
    joinable: false,
  },
];

interface PendingApproval {
  id: string;
  type: 'cr' | 'pipeline' | 'shepherd';
  title: string;
  source: string;
  age: string;
  primaryAction: { label: string; variant: 'accent' | 'outline' | 'ghost' };
  secondaryAction: { label: string; variant: 'accent' | 'outline' | 'ghost' };
}

const MOCK_APPROVALS: ReadonlyArray<PendingApproval> = [
  {
    id: 'approval-1',
    type: 'cr',
    title: 'CR-275102: OBO token refresh refactor',
    source: 'by akhilkot',
    age: '2 days',
    primaryAction: { label: 'Review', variant: 'accent' },
    secondaryAction: { label: 'Snooze', variant: 'ghost' },
  },
  {
    id: 'approval-2',
    type: 'cr',
    title: 'CR-275200: WAF rate limit tuning',
    source: 'by melwynas',
    age: '4 hours',
    primaryAction: { label: 'Review', variant: 'accent' },
    secondaryAction: { label: 'Snooze', variant: 'ghost' },
  },
  {
    id: 'approval-3',
    type: 'pipeline',
    title: 'WayneAIPlatform → Prod',
    source: 'beta passed, awaiting approval',
    age: '1 hour',
    primaryAction: { label: 'Approve', variant: 'accent' },
    secondaryAction: { label: 'View', variant: 'outline' },
  },
  {
    id: 'approval-4',
    type: 'shepherd',
    title: 'Token cache poisoning remediation',
    source: 'due in 3 days',
    age: '5 days',
    primaryAction: { label: 'File', variant: 'accent' },
    secondaryAction: { label: 'Snooze', variant: 'ghost' },
  },
  {
    id: 'approval-5',
    type: 'cr',
    title: 'CR-275033: Notification center component',
    source: 'by lsaik',
    age: '1 day',
    primaryAction: { label: 'Review', variant: 'accent' },
    secondaryAction: { label: 'Snooze', variant: 'ghost' },
  },
];

// ---------------------------------------------------------------------------
// Stale Work Mock Data
// ---------------------------------------------------------------------------

type StaleItemType = 'cr' | 'ticket' | 'workflow' | 'connection';

interface StaleItem {
  id: string;
  type: StaleItemType;
  description: string;
  age: string;
  source?: string;
  actions: ReadonlyArray<string>;
}

const MOCK_STALE_ITEMS: ReadonlyArray<StaleItem> = [
  {
    id: 'stale-1',
    type: 'cr',
    description: 'CR-274716 open for 8 days',
    age: '8d',
    source: 'Code Amazon',
    actions: ['Nudge reviewer', 'Close'],
  },
  {
    id: 'stale-2',
    type: 'ticket',
    description: 'T-1234567 no update in 12 days',
    age: '12d',
    source: 'SIM',
    actions: ['Update', 'Close'],
  },
  {
    id: 'stale-3',
    type: 'workflow',
    description: 'Weekly Security Scan failed silently 3 days ago',
    age: '3d',
    actions: ['View', 'Retry'],
  },
  {
    id: 'stale-4',
    type: 'connection',
    description: 'Quip token expires in 6 hours',
    age: '6h',
    actions: ['Reconnect'],
  },
  {
    id: 'stale-5',
    type: 'ticket',
    description: 'DSE-2415 blocked for 5 days',
    age: '5d',
    source: 'Jira',
    actions: ['Unblock', 'Reassign'],
  },
  {
    id: 'stale-6',
    type: 'cr',
    description: 'CR-275033 needs your review (3 days)',
    age: '3d',
    actions: ['Review', 'Decline'],
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getTimeOfDay(): 'morning' | 'afternoon' | 'evening' {
  const hour = new Date().getHours();
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}

function formatDayEyebrow(): string {
  const now = new Date();
  const weekday = now.toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase();
  const timeOfDay = getTimeOfDay().toUpperCase();
  const month = now.toLocaleDateString('en-US', { month: 'long' }).toUpperCase();
  const day = now.getDate();
  return `${weekday} ${timeOfDay} — ${month} ${day}`;
}

function getAlertCount(cards: ReadonlyArray<BriefingCard>): number {
  return cards.reduce((sum, c) => sum + (c.count ?? 1), 0);
}

function agentCssVar(agentId: string | undefined): string {
  if (!agentId) return 'var(--wayne-red)';
  return `var(--agent-${agentId})`;
}

function getStaleItemIcon(type: StaleItemType): JSX.Element {
  switch (type) {
    case 'cr':
      return <GitPullRequest size={14} className="text-wayne-orange shrink-0" />;
    case 'ticket':
      return <Ticket size={14} className="text-wayne-orange shrink-0" />;
    case 'workflow':
      return <Workflow size={14} className="text-wayne-orange shrink-0" />;
    case 'connection':
      return <Unplug size={14} className="text-wayne-orange shrink-0" />;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function Home(): JSX.Element {
  const navigate = useNavigate();
  const { user } = useAuth();
  const pageEnter = usePageEnter();

  const firstName = (user?.displayName ?? '').split(' ')[0] ?? '';
  const timeOfDay = getTimeOfDay();
  const greeting = firstName
    ? `Good ${timeOfDay}, ${firstName}.`
    : `Good ${timeOfDay}.`;
  const dayEyebrow = formatDayEyebrow();

  // Alerts state (dismissible)
  const [alerts, setAlerts] = useState<ReadonlyArray<BriefingCard>>(MOCK_ALERTS);
  // Pending approvals state (snoozable)
  const [approvals, setApprovals] = useState<ReadonlyArray<PendingApproval>>(MOCK_APPROVALS);
  // Stale work state (dismissible)
  const [staleItems, setStaleItems] = useState<ReadonlyArray<StaleItem>>(MOCK_STALE_ITEMS);
  const [staleExpanded, setStaleExpanded] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const updateScrollArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeftArrow(el.scrollLeft > 10);
    setShowRightArrow(el.scrollWidth - el.clientWidth - el.scrollLeft > 10);
  }, []);

  useEffect(() => {
    updateScrollArrows();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollArrows, { passive: true });
    const ro = new ResizeObserver(updateScrollArrows);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScrollArrows);
      ro.disconnect();
    };
  }, [alerts, updateScrollArrows]);

  const dismissCard = useCallback((cardId: string) => {
    setAlerts((prev) => prev.filter((c) => c.id !== cardId));
  }, []);

  const dismissAll = useCallback(() => {
    setAlerts([]);
  }, []);

  const handlePillClick = (seed: string): void => {
    navigate(`/play?seed=${encodeURIComponent(seed)}`);
  };

  const handleConversationClick = (seed: string): void => {
    navigate(`/play?seed=${encodeURIComponent(seed)}`);
  };

  const snoozeApproval = useCallback((approvalId: string) => {
    setApprovals((prev) => prev.filter((a) => a.id !== approvalId));
  }, []);

  const handleApprovalAction = useCallback((approval: PendingApproval) => {
    console.log(`[PendingApprovals] Action "${approval.primaryAction.label}" on:`, approval.title);
  }, []);

  // Stale work handlers
  const dismissStaleItem = useCallback((itemId: string) => {
    setStaleItems((prev) => prev.filter((item) => item.id !== itemId));
  }, []);

  const dismissAllStale = useCallback(() => {
    setStaleItems([]);
  }, []);

  // Tour wiring
  const { active: tourActive, start: startTour, complete: completeTour, skip: skipTour } =
    useTour('home', homeTourSteps);

  useEffect(() => {
    let pending: string | null = null;
    try {
      pending = window.sessionStorage.getItem(TOUR_START_STORAGE_KEY);
    } catch {
      pending = null;
    }
    if (pending === 'home') {
      try {
        window.sessionStorage.removeItem(TOUR_START_STORAGE_KEY);
      } catch {
        // ignore
      }
      startTour();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const alertCount = getAlertCount(alerts);

  const visibleStaleItems = staleExpanded ? staleItems : staleItems.slice(0, 5);
  const hiddenStaleCount = staleItems.length - 5;

  return (
    <div className={`py-12 px-16 max-w-[1200px] mx-auto ${pageEnter.className}`}>

      {/* ─── Header Row: Greeting (left) + Stats (right) ─── */}
      <div data-tour="home-welcome" className="flex flex-col lg:flex-row justify-between items-start gap-4 mb-8">
        {/* Left: Day eyebrow + Greeting */}
        <div>
          <p
            className="text-wayne-accent uppercase font-semibold mb-3"
            style={{ fontSize: 10, letterSpacing: '0.1em' }}
          >
            {dayEyebrow}
          </p>
          <h1
            className="font-serif text-wayne-ink"
            style={{ fontSize: 40, fontWeight: 500, letterSpacing: '-0.012em', lineHeight: 1.15 }}
          >
            {greeting}
          </h1>
        </div>

        {/* Right: Stats strip (inline) */}
        <div data-tour="home-active-agents" className="flex gap-6 flex-wrap lg:flex-nowrap pt-1">
          <HeaderStat label="Tokens" value={MOCK_STATS.tokensToday.toLocaleString()} />
          <HeaderStat label="Invocations" value={String(MOCK_STATS.invocations)} />
          <HeaderStat label="Tasks" value={String(MOCK_STATS.activeTasks)} />
          <HeaderStat label="Agents" value={String(MOCK_STATS.agentsActive)} />
        </div>
      </div>

      {/* ─── Main Body: Two-Column Grid ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-8 lg:items-start">

        {/* ═══ LEFT COLUMN: Action Required ═══ */}
        <div className="lg:max-h-[calc(100vh-200px)] lg:overflow-y-auto lg:pr-2" style={{ scrollbarWidth: 'thin' }}>
          {/* Column eyebrow */}
          <div className="flex items-center gap-2.5 mb-4">
            <p
              className="uppercase font-semibold text-wayne-accent"
              style={{ fontSize: 10, letterSpacing: '0.1em' }}
            >
              ACTION REQUIRED
            </p>
            {alertCount + approvals.length + staleItems.length > 0 && (
              <span
                className="inline-flex items-center justify-center rounded-full text-white font-bold"
                style={{
                  fontSize: 10,
                  minWidth: 18,
                  height: 18,
                  padding: '0 5px',
                  background: 'var(--wayne-red, #c0392b)',
                }}
              >
                {alertCount + approvals.length + staleItems.length}
              </span>
            )}
          </div>

          <div className="space-y-5">
            {/* ── Alerts ── */}
            <section data-tour="home-quick-prompts">
              {/* Section header */}
              <div className="flex items-center gap-3 mb-3">
                <p
                  className="uppercase font-semibold text-wayne-ink-3"
                  style={{ fontSize: 10, letterSpacing: '0.1em' }}
                >
                  ALERTS
                </p>
                {alertCount > 0 && (
                  <span
                    className="inline-flex items-center justify-center rounded-full text-white font-bold"
                    style={{
                      fontSize: 10,
                      minWidth: 18,
                      height: 18,
                      padding: '0 5px',
                      background: 'var(--wayne-red, #c0392b)',
                    }}
                  >
                    {alertCount}
                  </span>
                )}
                {alerts.length > 0 && (
                  <button
                    type="button"
                    onClick={dismissAll}
                    className="ml-auto uppercase font-medium text-wayne-ink-3 border border-wayne-hairline rounded px-2 py-0.5 hover:border-wayne-hairline-2 hover:bg-wayne-surface transition-colors cursor-pointer"
                    style={{ fontSize: 10, letterSpacing: '0.04em' }}
                  >
                    Dismiss all
                  </button>
                )}
              </div>

              {/* Cards scroll container */}
              {alerts.length === 0 ? (
                <div
                  className="text-wayne-ink-3 text-center border border-dashed border-wayne-hairline rounded-[14px] py-5 px-3"
                  style={{ fontSize: 13 }}
                >
                  All caught up — agents have nothing to report.
                </div>
              ) : (
                <div className="relative">
                  {/* Left arrow */}
                  {showLeftArrow && (
                    <button
                      type="button"
                      className="absolute left-[-16px] top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-wayne-surface border border-wayne-hairline text-wayne-ink-2 flex items-center justify-center shadow-sm hover:bg-wayne-surface-2 hover:border-wayne-hairline-2 cursor-pointer"
                      onClick={() => scrollRef.current?.scrollBy({ left: -340, behavior: 'smooth' })}
                      aria-label="Scroll left"
                    >
                      &#8249;
                    </button>
                  )}
                  {/* Right arrow */}
                  {showRightArrow && (
                    <button
                      type="button"
                      className="absolute right-[-16px] top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-wayne-surface border border-wayne-hairline text-wayne-ink-2 flex items-center justify-center shadow-sm hover:bg-wayne-surface-2 hover:border-wayne-hairline-2 cursor-pointer"
                      onClick={() => scrollRef.current?.scrollBy({ left: 340, behavior: 'smooth' })}
                      aria-label="Scroll right"
                    >
                      &#8250;
                    </button>
                  )}

                  <div
                    ref={scrollRef}
                    className="flex gap-3.5 overflow-x-auto pb-2 scroll-smooth snap-x snap-mandatory"
                    style={{ scrollbarWidth: 'thin' }}
                  >
                    {alerts.map((card, idx) => (
                      <BriefingCardItem
                        key={card.id}
                        card={card}
                        index={idx}
                        onDismiss={() => dismissCard(card.id)}
                        onPillClick={handlePillClick}
                      />
                    ))}
                  </div>
                </div>
              )}
            </section>

            {/* ── Pending Approvals ── */}
            <section>
              <div className="flex items-center gap-3 mb-3">
                <p
                  className="uppercase font-semibold text-wayne-ink-3"
                  style={{ fontSize: 10, letterSpacing: '0.1em' }}
                >
                  PENDING APPROVALS
                </p>
                <span
                  className="inline-flex items-center justify-center text-white font-bold"
                  style={{
                    fontSize: 10,
                    minWidth: 18,
                    height: 18,
                    padding: '0 5px',
                    borderRadius: 9,
                    background: 'var(--wayne-accent)',
                  }}
                >
                  {approvals.length}
                </span>
                <button
                  type="button"
                  className="ml-auto text-wayne-ink-3 hover:text-wayne-ink transition-colors cursor-pointer"
                  style={{ fontSize: 11 }}
                  onClick={() => { console.log('[PendingApprovals] View all'); }}
                >
                  View all
                </button>
              </div>
              <div
                className="flex gap-3.5 overflow-x-auto snap-x pb-2"
                style={{ scrollbarWidth: 'thin' }}
              >
                {approvals.map((approval, idx) => (
                  <ApprovalCard
                    key={approval.id}
                    approval={approval}
                    index={idx}
                    onPrimaryAction={() => handleApprovalAction(approval)}
                    onSecondaryAction={() => snoozeApproval(approval.id)}
                  />
                ))}
              </div>
            </section>

            {/* ── Stale Work ── */}
            <section>
              {/* Section header */}
              <div className="flex items-center gap-3 mb-3">
                <div className="flex items-center gap-1.5">
                  <AlertTriangle size={12} className="text-wayne-orange" />
                  <p
                    className="uppercase font-semibold text-wayne-orange"
                    style={{ fontSize: 10, letterSpacing: '0.1em' }}
                  >
                    STALE WORK
                  </p>
                </div>
                {staleItems.length > 0 && (
                  <span
                    className="inline-flex items-center justify-center text-wayne-orange font-bold"
                    style={{
                      fontSize: 10,
                      minWidth: 18,
                      height: 18,
                      padding: '0 6px',
                      borderRadius: 9,
                      background: 'var(--wayne-orange-10, rgba(234, 137, 54, 0.1))',
                    }}
                  >
                    {staleItems.length}
                  </span>
                )}
                {staleItems.length > 0 && (
                  <button
                    type="button"
                    onClick={dismissAllStale}
                    className="ml-auto text-wayne-ink-3 hover:text-wayne-ink transition-colors cursor-pointer"
                    style={{ fontSize: 11 }}
                  >
                    Dismiss all
                  </button>
                )}
              </div>

              {/* Content */}
              {staleItems.length === 0 ? (
                <div className="border border-wayne-hairline rounded-xl py-6 flex flex-col items-center justify-center gap-2">
                  <CheckCircle size={20} className="text-wayne-green" />
                  <span className="text-wayne-ink-3" style={{ fontSize: 13 }}>
                    All clear — nothing going stale.
                  </span>
                </div>
              ) : (
                <div className="border border-wayne-hairline rounded-xl overflow-hidden">
                  {visibleStaleItems.map((item, idx) => (
                    <StaleWorkRow
                      key={item.id}
                      item={item}
                      index={idx}
                      onDismiss={dismissStaleItem}
                    />
                  ))}
                  {!staleExpanded && hiddenStaleCount > 0 && (
                    <button
                      type="button"
                      onClick={() => setStaleExpanded(true)}
                      className="w-full px-4 py-2.5 text-center text-wayne-accent hover:bg-wayne-surface-2 transition-colors cursor-pointer border-t border-wayne-hairline"
                      style={{ fontSize: 12, fontWeight: 500 }}
                    >
                      Show {hiddenStaleCount} more
                    </button>
                  )}
                </div>
              )}
            </section>
          </div>
        </div>

        {/* ═══ RIGHT COLUMN: Your Day ═══ */}
        <div className="lg:sticky lg:top-6">
          {/* Column eyebrow */}
          <p
            className="uppercase font-semibold text-wayne-ink-3 mb-4"
            style={{ fontSize: 10, letterSpacing: '0.1em' }}
          >
            YOUR DAY
          </p>

          {/* Today's Schedule */}
          <TodaysSchedule meetings={MOCK_SCHEDULE} />

          {/* Recent Conversations */}
          <section data-tour="home-recent-conversations">
            <p
              className="uppercase font-semibold text-wayne-ink-3 mb-2.5"
              style={{ fontSize: 10, letterSpacing: '0.1em' }}
            >
              RECENT
            </p>
            <div className="flex gap-3 flex-wrap">
              {MOCK_CONVERSATIONS.map((conv) => (
                <button
                  key={conv.id}
                  type="button"
                  onClick={() => handleConversationClick(conv.seed)}
                  className="flex items-center gap-2 bg-wayne-surface border border-wayne-hairline rounded-lg px-3.5 py-2 cursor-pointer hover:border-wayne-hairline-2 hover:shadow-[0_1px_4px_rgba(0,0,0,0.04)] transition-all active:scale-[0.97]"
                >
                  <span
                    className="w-[7px] h-[7px] rounded-full shrink-0"
                    style={{ background: agentCssVar(conv.agentId) }}
                  />
                  <span className="text-wayne-ink font-medium" style={{ fontSize: 13 }}>
                    {conv.title}
                  </span>
                  <span className="text-wayne-ink-3" style={{ fontSize: 11 }}>
                    {conv.time}
                  </span>
                </button>
              ))}
            </div>
          </section>
        </div>
      </div>

      {/* Tour overlay */}
      <GuidedTour
        tourId="home"
        steps={homeTourSteps}
        active={tourActive}
        onComplete={completeTour}
        onSkip={skipTour}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface BriefingCardItemProps {
  card: BriefingCard;
  index: number;
  onDismiss: () => void;
  onPillClick: (seed: string) => void;
}

function BriefingCardItem({ card, index, onDismiss, onPillClick }: BriefingCardItemProps): JSX.Element {
  const stripeColor = card.critical ? 'var(--wayne-red)' : agentCssVar(card.agentId);
  const isCritical = card.critical === true;

  return (
    <div
      className={[
        'relative bg-wayne-surface border border-wayne-hairline rounded-[14px] p-5 flex flex-col gap-1 min-w-[320px] max-w-[360px] shrink-0 snap-start',
        'hover:border-wayne-hairline-2 hover:shadow-[0_2px_8px_rgba(0,0,0,0.04)]',
        'transition-all',
        isCritical ? 'animate-[criticalPulse_2s_ease-in-out_infinite]' : '',
      ].join(' ')}
      style={{
        animationDelay: `${index * 50}ms`,
      }}
    >
      {/* Left stripe */}
      <span
        className="absolute left-0 top-0 bottom-0 w-[3px] rounded-l-[14px]"
        style={{ background: stripeColor }}
        aria-hidden="true"
      />

      {/* Header row */}
      <div className="flex items-center gap-2 mb-1">
        {isCritical ? (
          <span
            className="font-bold uppercase"
            style={{ fontSize: 12, color: 'var(--wayne-red)', letterSpacing: '0.04em' }}
          >
            CRITICAL
          </span>
        ) : (
          <>
            <span
              className="w-[7px] h-[7px] rounded-full shrink-0"
              style={{ background: stripeColor }}
            />
            <span
              className="font-mono font-semibold uppercase"
              style={{ fontSize: 12, letterSpacing: '0.03em', color: stripeColor }}
            >
              {card.agentName}
            </span>
            {card.count !== undefined && (
              <span
                className="inline-flex items-center justify-center rounded-full text-white font-bold ml-auto"
                style={{
                  fontSize: 10,
                  minWidth: 18,
                  height: 18,
                  padding: '0 5px',
                  background: 'var(--wayne-accent)',
                }}
              >
                {card.count}
              </span>
            )}
          </>
        )}
        <button
          type="button"
          onClick={onDismiss}
          className="ml-auto w-5 h-5 rounded-full bg-wayne-hairline text-wayne-ink-3 flex items-center justify-center text-sm hover:text-wayne-ink hover:bg-wayne-hairline-2 transition-colors cursor-pointer shrink-0"
          aria-label="Dismiss"
        >
          &times;
        </button>
      </div>

      {/* Headline */}
      <span className="text-wayne-ink font-semibold" style={{ fontSize: 14, lineHeight: 1.35 }}>
        {isCritical && '⚠ '}{card.headline}
      </span>

      {/* Subtitle */}
      <span className="text-wayne-ink-2" style={{ fontSize: 13, lineHeight: 1.4 }}>
        {card.subtitle}
      </span>

      {/* Action pills */}
      <div className="flex flex-wrap gap-1.5 mt-2.5">
        {card.pills.map((pill) => (
          <button
            key={pill.seed}
            type="button"
            onClick={() => onPillClick(pill.seed)}
            className="text-wayne-ink-2 border border-wayne-hairline rounded-full px-2.5 py-1 hover:border-wayne-accent hover:text-wayne-accent hover:bg-wayne-surface-2 transition-all active:scale-[0.95] cursor-pointer"
            style={{ fontSize: 11, fontWeight: 500 }}
          >
            {pill.label}
          </button>
        ))}
      </div>
    </div>
  );
}

interface ApprovalCardProps {
  approval: PendingApproval;
  index: number;
  onPrimaryAction: () => void;
  onSecondaryAction: () => void;
}

function ApprovalCard({ approval, index, onPrimaryAction, onSecondaryAction }: ApprovalCardProps): JSX.Element {
  const { className: staggerClass, style: staggerStyle } = useStaggerAnimation(index);

  const TypeIcon = approval.type === 'cr'
    ? GitPullRequest
    : approval.type === 'pipeline'
      ? Rocket
      : Shield;

  const typeLabel = approval.type === 'cr'
    ? 'CR Review'
    : approval.type === 'pipeline'
      ? 'Pipeline Promotion'
      : 'Shepherd Risk';

  return (
    <div
      className={[
        'bg-wayne-surface border border-wayne-hairline rounded-xl p-4 min-w-[280px] max-w-[320px] shrink-0',
        'transition-all active:scale-[0.97]',
        'hover:border-wayne-hairline-2 hover:shadow-[0_2px_8px_rgba(0,0,0,0.04)]',
        staggerClass,
      ].join(' ')}
      style={staggerStyle}
    >
      {/* Top row: icon + type + age */}
      <div className="flex items-center gap-1.5 mb-2">
        <TypeIcon size={13} className="text-wayne-ink-2 shrink-0" />
        <span
          className="font-mono uppercase text-wayne-ink-2"
          style={{ fontSize: 10, letterSpacing: '0.03em' }}
        >
          {typeLabel}
        </span>
        <span className="ml-auto text-wayne-ink-3" style={{ fontSize: 11 }}>
          {approval.age}
        </span>
      </div>

      {/* Title */}
      <p
        className="text-wayne-ink font-medium line-clamp-1 mb-1"
        style={{ fontSize: 14 }}
      >
        {approval.title}
      </p>

      {/* Author/source */}
      <p className="text-wayne-ink-3 mb-3" style={{ fontSize: 12 }}>
        {approval.source}
      </p>

      {/* Action buttons */}
      <div className="flex gap-2">
        <AppleButton
          variant={approval.primaryAction.variant}
          className="!text-[11px] !px-3 !py-1 !rounded-full"
          onClick={onPrimaryAction}
        >
          {approval.primaryAction.label}
        </AppleButton>
        <AppleButton
          variant={approval.secondaryAction.variant}
          className="!text-[11px] !px-3 !py-1 !rounded-full"
          onClick={onSecondaryAction}
        >
          {approval.secondaryAction.label}
        </AppleButton>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stale Work Row
// ---------------------------------------------------------------------------

interface StaleWorkRowProps {
  item: StaleItem;
  index: number;
  onDismiss: (id: string) => void;
}

function StaleWorkRow({ item, index, onDismiss }: StaleWorkRowProps): JSX.Element {
  const stagger = useStaggerAnimation(index);

  return (
    <div
      className={`px-4 py-3 flex items-center gap-3 hover:bg-wayne-surface-2 transition-colors border-b border-wayne-hairline last:border-b-0 ${stagger.className}`}
      style={stagger.style}
    >
      {/* Icon */}
      {getStaleItemIcon(item.type)}

      {/* Description + age */}
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <span className="text-wayne-ink truncate" style={{ fontSize: 13 }}>
          {item.description}
        </span>
        <span
          className="text-wayne-orange font-mono shrink-0"
          style={{ fontSize: 10 }}
        >
          {item.age}
        </span>
      </div>

      {/* Source pill */}
      {item.source && (
        <span
          className="shrink-0 uppercase border border-wayne-hairline rounded px-1.5 py-0.5 text-wayne-ink-3"
          style={{ fontSize: 9, letterSpacing: '0.04em' }}
        >
          {item.source}
        </span>
      )}

      {/* Action buttons */}
      <div className="flex items-center gap-1.5 shrink-0">
        {item.actions.map((action) => (
          <button
            key={action}
            type="button"
            onClick={() => onDismiss(item.id)}
            className="text-wayne-ink-3 hover:text-wayne-ink transition-colors cursor-pointer rounded px-1.5 py-0.5"
            style={{ fontSize: 11 }}
          >
            {action}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header Stat (compact inline stat for the header row)
// ---------------------------------------------------------------------------

interface HeaderStatProps {
  label: string;
  value: string;
}

function HeaderStat({ label, value }: HeaderStatProps): JSX.Element {
  return (
    <div className="text-right">
      <div
        className="font-serif text-wayne-ink"
        style={{ fontSize: 20, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}
      >
        {value}
      </div>
      <div
        className="text-wayne-ink-3 uppercase font-medium"
        style={{ fontSize: 10, letterSpacing: '0.08em' }}
      >
        {label}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Today's Schedule
// ---------------------------------------------------------------------------

interface TodaysScheduleProps {
  meetings: ReadonlyArray<ScheduleMeeting>;
}

function TodaysSchedule({ meetings }: TodaysScheduleProps): JSX.Element {
  const todayDate = new Date().toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

  return (
    <section className="mb-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Calendar className="w-3.5 h-3.5 text-wayne-ink-3" />
          <p
            className="uppercase font-semibold text-wayne-ink-3"
            style={{ fontSize: 10, letterSpacing: '0.1em' }}
          >
            TODAY&apos;S SCHEDULE
          </p>
        </div>
        <span
          className="font-mono text-wayne-ink-3"
          style={{ fontSize: 11 }}
        >
          {todayDate}
        </span>
      </div>

      {/* Meeting list container */}
      <div className="bg-wayne-surface border border-wayne-hairline rounded-xl overflow-hidden">
        {meetings.map((meeting, idx) => (
          <ScheduleRow
            key={meeting.id}
            meeting={meeting}
            isLast={idx === meetings.length - 1}
          />
        ))}
      </div>
    </section>
  );
}

interface ScheduleRowProps {
  meeting: ScheduleMeeting;
  isLast: boolean;
}

function ScheduleRow({ meeting, isLast }: ScheduleRowProps): JSX.Element {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className={[
        'px-4 py-3 hover:bg-wayne-surface-2 active:scale-[0.99] transition-all cursor-pointer select-none',
        !isLast ? 'border-b border-wayne-hairline' : '',
      ].join(' ')}
      onClick={() => setExpanded((prev) => !prev)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setExpanded((prev) => !prev);
        }
      }}
      aria-expanded={expanded}
    >
      <div className="flex items-center gap-3">
        {/* Time */}
        <span
          className="font-mono text-wayne-ink shrink-0"
          style={{ fontSize: 13, minWidth: 72 }}
        >
          {meeting.time}
        </span>

        {/* Title + duration + prep */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span
              className="text-wayne-ink font-medium truncate"
              style={{ fontSize: 14 }}
            >
              {meeting.title}
            </span>
            <span
              className="shrink-0 rounded-full bg-wayne-surface-2 px-2 text-wayne-ink-3"
              style={{ fontSize: 10, lineHeight: '18px' }}
            >
              {meeting.duration}
            </span>
          </div>
          {!expanded && (
            <p
              className="text-wayne-ink-3 italic line-clamp-1 mt-0.5"
              style={{ fontSize: 12 }}
            >
              {meeting.prep}
            </p>
          )}
          {expanded && (
            <p
              className="text-wayne-ink-3 italic mt-0.5"
              style={{ fontSize: 12 }}
            >
              {meeting.prep}
            </p>
          )}
        </div>

        {/* Join button (only shown if joinable) */}
        {meeting.joinable && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
            }}
            className="shrink-0 flex items-center gap-1.5 border border-wayne-hairline rounded-full px-3 py-1 text-wayne-ink-2 hover:border-wayne-accent hover:text-wayne-accent transition-colors cursor-pointer"
            style={{ fontSize: 12, fontWeight: 500 }}
          >
            <Video className="w-3 h-3" />
            Join
          </button>
        )}

        {/* Expand chevron */}
        <ChevronDown
          className={[
            'w-4 h-4 text-wayne-ink-3 shrink-0 transition-transform',
            expanded ? 'rotate-180' : '',
          ].join(' ')}
        />
      </div>
    </div>
  );
}
